import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  entryFromEventFragment,
  Heading,
  Input,
  PaymentIdentifier,
  PaymentIdentifierLabel,
  PrimaryFullScreenSpinner,
  Spinner,
  TimeLog,
  Txt,
  toast,
} from "@repo/ui";
import { NotFoundError, prettyPrintEnum } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { RiCloseCircleLine, RiPencilLine } from "react-icons/ri";
import { PaymentIdentifiersCard } from "~/components/BeneficiaryAccount/PaymentIdentifiers";
import { BeneficiaryAccountStatusBadge } from "~/components/BeneficiaryAccount/statusBadge";
import { AdminCurrencycloudBeneficiaryCard } from "~/components/Currencycloud/AdminCurrencycloudBeneficiaryCard";
import { DetailRow, DetailSubRow } from "~/components/DetailRow/DetailRow";
import { AdminEburyBeneficiaryCard } from "~/components/Ebury/AdminEburyBeneficiaryCard";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/beneficiary-accounts_/$beneficiaryAccountId",
)({
  head: () => opsHead("Beneficiary Account"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { beneficiaryAccountId } = Route.useParams();
  const [open, setOpen] = useState(false);
  if (!beneficiaryAccountId) {
    throw new NotFoundError();
  }

  const [updateBeneficiaryAccountStatus, { loading: mutationLoading }] =
    useMutation(
      graphql(`
    mutation UpdateBeneficiaryAccountStatus($id: ID!, $newStatus: BeneficiaryAccountStatus!) {
      updateBeneficiaryAccountStatus(input: {id: $id, newStatus: $newStatus}) {
        beneficiaryAccount {
          id
          status
        }
      }
    }`),
    );

  const [updateAccountDetailsMutation] = useMutation(
    graphql(`
    mutation AdminUpdateBeneficiaryAccountDetails($input: UpdateBeneficiaryAccountDetailsInput!) {
      updateBeneficiaryAccountDetails(input: $input) {
        beneficiaryAccount {
          id
          nickname
          country
          accountName
        }
      }
    }`),
  );

  const disableAccount = async () => {
    try {
      await updateBeneficiaryAccountStatus({
        variables: {
          newStatus: "DISABLED",
          id: beneficiaryAccountId,
        },
      });
      toast({
        title: "Account disabled",
        kind: "success",
        autoHide: true,
      });
    } catch {
      toast({
        title: "Something went wrong disabling the account",
        kind: "error",
        autoHide: true,
      });
    } finally {
      setOpen(false);
    }
  };

  const activateAccount = async () => {
    await updateBeneficiaryAccountStatus({
      variables: {
        newStatus: "ACTIVE",
        id: beneficiaryAccountId,
      },
      onCompleted() {
        toast({
          title: "Account activated",
          kind: "success",
          autoHide: true,
        });
      },
      onError(e) {
        const description = e.graphQLErrors.at(0)?.message;
        toast({
          title: "Something went wrong",
          kind: "error",
          description,
        });
      },
    });
    setOpen(false);
  };

  const { data, error } = useSuspenseQuery(
    graphql(`
    query AdminBeneficiaryAccount($id: ID!) {
        beneficiaryAccount(id: $id) {
            id
            accountName
            nickname
            currency
            country
            status
            kind
            businessEntity {
              id
              currencycloudAccounts {
                id
              }
              eburyClients {
                id
              }
            }
            paymentIdentifiers {
              id
              ... PaymentIdentifierComponent
            }
            paymentIdentifierEditable
            address {
              id
              buildingNumber
              streetName
              city
              state
              postcode
              country
            }
            businessEntity {
              id
              name
              organisation {
                id
                name
              }
            }
            events {
              ...TimeLogEventEntry
            }
            ... @defer {
              eburyBeneficiary {
                id
                ...AdminEburyBeneficiaryCard
              }
            }
            ... @defer {
              currencycloudBeneficiary {
                id
                ...AdminCurrencycloudBeneficiaryCard
              }
            }
          }
    }`),
    { variables: { id: beneficiaryAccountId } },
  );

  const [editingNickname, setEditingNickname] = useState(false);
  const [editingCountry, setEditingCountry] = useState(false);
  const [editingAccountName, setEditingAccountName] = useState(false);

  const beneficiaryAccount = data?.beneficiaryAccount;
  if (!beneficiaryAccount) {
    throw new NotFoundError();
  }

  useOpsTitle(
    `${beneficiaryAccount.nickname || beneficiaryAccount.accountName} - Beneficiary Account`,
  );

  const updateNickname = async (name: string) => {
    setEditingNickname(false);
    await updateAccountDetailsMutation({
      variables: {
        input: {
          beneficiaryAccountId,
          nickname: name,
        },
      },
      optimisticResponse: {
        __typename: "Mutation",
        updateBeneficiaryAccountDetails: {
          __typename: "UpdateBeneficiaryAccountDetailsPayload",
          beneficiaryAccount: {
            __typename: "BeneficiaryAccount",
            id: beneficiaryAccountId,
            nickname: name,
            country: beneficiaryAccount.country,
            accountName: beneficiaryAccount.accountName,
          },
        },
      },
      onError(e) {
        toast({
          title: "Something went wrong updating the account",
          kind: "error",
          autoHide: true,
          description: e.graphQLErrors.at(0)?.message,
        });
      },
    });
  };

  const updateAccountName = async (name: string) => {
    setEditingAccountName(false);
    await updateAccountDetailsMutation({
      variables: {
        input: {
          beneficiaryAccountId,
          accountName: name,
        },
      },
      optimisticResponse: {
        __typename: "Mutation",
        updateBeneficiaryAccountDetails: {
          __typename: "UpdateBeneficiaryAccountDetailsPayload",
          beneficiaryAccount: {
            __typename: "BeneficiaryAccount",
            id: beneficiaryAccountId,
            nickname: beneficiaryAccount.nickname,
            country: beneficiaryAccount.country,
            accountName: name,
          },
        },
      },
      onError(e) {
        toast({
          title: "Something went wrong updating the account",
          kind: "error",
          autoHide: true,
          description: e.graphQLErrors.at(0)?.message,
        });
      },
    });
  };

  const updateCountry = async (country: string) => {
    setEditingCountry(false);
    await updateAccountDetailsMutation({
      variables: {
        input: {
          beneficiaryAccountId,
          country,
        },
      },
      optimisticResponse: {
        __typename: "Mutation",
        updateBeneficiaryAccountDetails: {
          __typename: "UpdateBeneficiaryAccountDetailsPayload",
          beneficiaryAccount: {
            __typename: "BeneficiaryAccount",
            id: beneficiaryAccountId,
            nickname: beneficiaryAccount.nickname,
            country,
            accountName: beneficiaryAccount.accountName,
          },
        },
      },
      onError(e) {
        toast({
          title: "Something went wrong updating the account",
          kind: "error",
          autoHide: true,
          description: e.graphQLErrors.at(0)?.message,
        });
      },
    });
  };

  const {
    accountName,
    businessEntity,
    currency,
    country,
    id,
    nickname,
    status,
    eburyBeneficiary,
    currencycloudBeneficiary,
    kind,
    address,
  } = beneficiaryAccount;

  return (
    <AdminPageContainer>
      <AdminPageHeader title={nickname || accountName} />

      <div className="flex flex-col gap-4">
        {status === "ACTIVE" && (
          <div className="flex flex-row justify-end">
            <Dialog onOpenChange={setOpen} open={open}>
              <DialogTrigger asChild>
                <Button variant="destructive">Disable account</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Disable beneficiary account</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to disable this beneficiary account?
                    <br />
                    It will no longer be able to be used for payouts.
                  </DialogDescription>
                </DialogHeader>

                <DialogFooter className="gap-2 sm:justify-between">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={(e) => {
                      setOpen(false);
                      e.preventDefault();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={mutationLoading}
                    onClick={disableAccount}
                    variant="destructive"
                  >
                    {mutationLoading ? <Spinner /> : "Disable account"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {status === "PENDING" && (
          <div className="flex flex-row justify-end">
            <Dialog onOpenChange={setOpen} open={open}>
              <DialogTrigger asChild>
                <Button>Activate account</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Activate beneficiary account</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to activate this beneficiary account?
                  </DialogDescription>
                </DialogHeader>

                <Txt>Payment identifiers</Txt>
                {beneficiaryAccount.paymentIdentifiers?.map(
                  (paymentIdentifier) => (
                    <Card key={paymentIdentifier.id}>
                      <CardHeader className="py-2">
                        <CardTitle>
                          <Heading size="sm" as="h3">
                            <PaymentIdentifierLabel
                              paymentIdentifier={paymentIdentifier}
                            />
                          </Heading>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <PaymentIdentifier
                          paymentIdentifier={paymentIdentifier}
                        />
                      </CardContent>
                    </Card>
                  ),
                )}

                <DialogFooter className="gap-2 sm:justify-between">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={(e) => {
                      setOpen(false);
                      e.preventDefault();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button disabled={mutationLoading} onClick={activateAccount}>
                    {mutationLoading ? <Spinner /> : "Activate account"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        <div className="flex flex-row gap-4">
          <Card className="flex-grow p-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>
                <Heading size="2xl" as="h2">
                  Beneficiary account details
                </Heading>
              </CardTitle>
            </CardHeader>

            <CardContent className="w-full">
              <DetailRow
                label="Status"
                value={<BeneficiaryAccountStatusBadge status={status} />}
                className=""
              />
              <DetailRow
                label="Organisation"
                value={
                  businessEntity && businessEntity.organisation ? (
                    <Link
                      to="/organisations/$organisationId"
                      params={{
                        organisationId: businessEntity.organisation.id,
                      }}
                      className="underline"
                    >
                      {businessEntity.organisation.name}
                    </Link>
                  ) : (
                    "Unknown"
                  )
                }
              />
              <DetailRow
                label="Business Entity"
                value={
                  businessEntity && businessEntity.organisation ? (
                    <Link
                      to="/organisations/$organisationId/business-entities/$businessEntityId"
                      params={{
                        organisationId: businessEntity.organisation.id,
                        businessEntityId: businessEntity.id,
                      }}
                      className="underline"
                    >
                      {businessEntity.name}
                    </Link>
                  ) : (
                    "Unknown TODO LINK"
                  )
                }
              />
              <DetailRow
                label="Nickname"
                value={
                  editingNickname ? (
                    <div className="flex w-1/3 flex-row items-center gap-2">
                      <Input
                        autoFocus
                        defaultValue={nickname}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            void updateNickname(e.currentTarget.value);
                          } else if (e.key === "Escape") {
                            setEditingNickname(false);
                          }
                        }}
                      />
                      <RiCloseCircleLine
                        className="size-6 cursor-pointer text-secondary"
                        onClick={() => setEditingNickname(false)}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-row items-center gap-4">
                      <Txt>{nickname}</Txt>
                      <RiPencilLine
                        className="cursor-pointer"
                        onClick={() => setEditingNickname(true)}
                      />
                    </div>
                  )
                }
              />
              <DetailRow
                label="Account Name"
                value={
                  editingAccountName ? (
                    <div className="flex w-1/3 flex-row items-center gap-2">
                      <Input
                        autoFocus
                        defaultValue={accountName}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            void updateAccountName(e.currentTarget.value);
                          } else if (e.key === "Escape") {
                            setEditingAccountName(false);
                          }
                        }}
                      />
                      <RiCloseCircleLine
                        className="size-6 cursor-pointer text-secondary"
                        onClick={() => setEditingAccountName(false)}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-row items-center gap-4">
                      <Txt>{accountName}</Txt>
                      {status === "PENDING" && (
                        <RiPencilLine
                          className="cursor-pointer"
                          onClick={() => setEditingAccountName(true)}
                        />
                      )}
                    </div>
                  )
                }
              />
              <DetailRow label="Currency" value={currency} />
              <DetailRow
                label="Country"
                value={
                  editingCountry ? (
                    <div className="flex w-1/3 flex-row items-center gap-2">
                      <Input
                        autoFocus
                        defaultValue={country}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            void updateCountry(e.currentTarget.value);
                          } else if (e.key === "Escape") {
                            setEditingCountry(false);
                          }
                        }}
                      />
                      <RiCloseCircleLine
                        className="size-6 cursor-pointer text-secondary"
                        onClick={() => setEditingCountry(false)}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-row items-center gap-4">
                      <Txt>{country}</Txt>
                      {status === "PENDING" && (
                        <RiPencilLine
                          className="cursor-pointer"
                          onClick={() => setEditingCountry(true)}
                        />
                      )}
                    </div>
                  )
                }
              />
              <DetailRow label="Kind" value={prettyPrintEnum(kind)} />
              {address && (
                <DetailRow
                  label="Address"
                  value={
                    <div className="flex flex-col gap-2">
                      <DetailSubRow
                        label="Building number"
                        value={address.buildingNumber}
                      />
                      <DetailSubRow
                        label="Street name"
                        value={address.streetName}
                      />
                      <DetailSubRow label="City" value={address.city} />
                      <DetailSubRow label="State" value={address.state} />
                      <DetailSubRow label="Postcode" value={address.postcode} />
                      <DetailSubRow label="Country" value={address.country} />
                    </div>
                  }
                />
              )}
              <DetailRow label="ID" value={id} />
            </CardContent>
          </Card>

          <PaymentIdentifiersCard beneficiaryAccount={beneficiaryAccount} />
        </div>

        <div className="flex flex-row gap-4">
          {(beneficiaryAccount.businessEntity?.eburyClients ?? []).length >
            0 && (
            <Card className="p-2">
              <CardHeader>
                <CardTitle>
                  <Heading size="2xl" as="h2">
                    Ebury
                  </Heading>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <Txt as="p">
                  Sync this beneficiary account to allow it to be used as a
                  beneficiary of Ebury transactions
                </Txt>

                <AdminEburyBeneficiaryCard
                  eburyBeneficiary={eburyBeneficiary}
                  beneficiaryAccountId={beneficiaryAccount.id}
                  errors={error?.graphQLErrors?.filter((gqlError) => {
                    return (
                      gqlError.path?.at(0) === "beneficiaryAccount" &&
                      gqlError.path?.at(1) === "eburyBeneficiary"
                    );
                  })}
                />
              </CardContent>
            </Card>
          )}

          {(beneficiaryAccount.businessEntity?.currencycloudAccounts ?? [])
            .length > 0 && (
            <Card className="p-2">
              <CardHeader>
                <CardTitle>
                  <Heading size="2xl" as="h2">
                    Visa
                  </Heading>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <Txt as="p">
                  Sync this beneficiary account to allow it to be used as a
                  beneficiary of Visa transactions
                </Txt>

                <AdminCurrencycloudBeneficiaryCard
                  currencycloudBeneficiary={currencycloudBeneficiary}
                  beneficiaryAccountId={beneficiaryAccount.id}
                  errors={error?.graphQLErrors?.filter((gqlError) => {
                    return (
                      gqlError.path?.at(0) === "beneficiaryAccount" &&
                      gqlError.path?.at(1) === "currencycloudBeneficiary"
                    );
                  })}
                />
              </CardContent>
            </Card>
          )}
        </div>
        <Card className="w-full p-2">
          <CardHeader>
            <CardTitle>
              <Heading size="2xl" as="h2">
                Event log
              </Heading>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {beneficiaryAccount.events && (
              <TimeLog
                entries={beneficiaryAccount.events.map(entryFromEventFragment)}
              />
            )}
            {(beneficiaryAccount.events?.length ?? 0) === 0 && (
              <div className="text-center">No events</div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminPageContainer>
  );
}
